import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import '../styles/pages/AffiliateAdmin.css';

/**
 * AffiliateAdminPage - アフィリエイト商品管理画面（管理者専用認証）
 */
const AffiliateAdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('products'); // 'products' | 'widget' | 'stats'
  const [message, setMessage] = useState({ type: '', text: '' });
  const [categoryFilter, setCategoryFilter] = useState('all'); // カテゴリーフィルター

  // 商品編集モーダル状態
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);

  // 商品フォームデータ
  const [productForm, setProductForm] = useState({
    name: '',
    category: '醤油',
    brand: '',
    description: '',
    medicalRationale: '',
    freeFrom: [],
    images: {
      primary: {
        url: '',
        source: 'uploaded',
        altText: ''
      }
    },
    affiliateService: '', // 'google_adsense' | 'moshimo' | 'amazon' | 'rakuten'
    adsenseCode: '',
    rakutenType: '', // 'affiliate' | 'room'
    rakutenRoomCode: '',
    affiliateLinks: {
      moshimo: {
        enabled: false,
        htmlCode: ''
      },
      rakutenDirect: {
        enabled: false,
        affiliateUrl: '',
        htmlCode: '',
        commissionRate: 0,
        isRateUpProduct: false
      },
      amazonDirect: {
        enabled: false,
        affiliateUrl: '',
        asin: ''
      }
    },
    displayLocations: [],
    relatedTags: [],
    isActive: true,
    displayOrder: 0
  });

  // 画像アップロード状態
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    document.title = 'アフィリエイト管理 | つむぎ';

    // 管理者認証を確認
    const checkAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin');
        return;
      }

      try {
        const response = await api.get('/api/admin/verify', {
          headers: { 'X-Admin-Token': token }
        });
        if (response.data.success) {
          setIsAuthenticated(true);
          loadProducts();
          loadWidgetConfig();
        } else {
          navigate('/admin');
        }
      } catch (error) {
        navigate('/admin');
      }
      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  // APIリクエストに管理者トークンを付加するヘルパー
  const adminApi = {
    get: (url) => api.get(url, {
      headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
    }),
    post: (url, data, config = {}) => api.post(url, data, {
      ...config,
      headers: { ...config.headers, 'X-Admin-Token': localStorage.getItem('adminToken') }
    }),
    put: (url, data) => api.put(url, data, {
      headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
    }),
    delete: (url) => api.delete(url, {
      headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
    }),
    patch: (url) => api.patch(url, {}, {
      headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
    })
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/api/affiliate/admin/products/all');
      if (response.data.success) {
        setProducts(response.data.products);
      }
    } catch (error) {
      console.error('商品読み込みエラー:', error);
      showMessage('error', '商品の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadWidgetConfig = async () => {
    try {
      const response = await adminApi.get('/api/affiliate/widget-config');
      if (response.data.success) {
        setWidgetConfig(response.data.config);
      }
    } catch (error) {
      console.error('ウィジェット設定読み込みエラー:', error);
    }
  };

  const updateWidgetConfig = async (configUpdates) => {
    try {
      const response = await adminApi.put('/api/affiliate/admin/widget-config', configUpdates);
      if (response.data.success) {
        // 保存成功時は state を上書きしない（local state が正しい値を持つ）。
        // 理由:
        //   - PUT response は Mongoose 内部の field 名（adsenseConfig 等）で
        //     public GET の shape（adsense 等）と異なる
        //   - public GET は AdSense の clientId / slotId 等の admin-only 情報を
        //     返さないため、reload すると input が空欄になる
        showMessage('success', 'ウィジェット設定を保存しました');
      } else {
        showMessage('error', 'ウィジェット設定の保存に失敗しました');
      }
    } catch (error) {
      console.error('ウィジェット設定更新エラー:', error);
      showMessage('error', 'ウィジェット設定の保存に失敗しました');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ファイルサイズチェック（5MB）
    if (file.size > 5 * 1024 * 1024) {
      showMessage('error', '画像サイズは5MB以下にしてください');
      return;
    }

    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('image', file);

      const response = await adminApi.post('/api/affiliate/admin/upload-product-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setProductForm(prev => ({
          ...prev,
          images: {
            ...prev.images,
            primary: {
              url: response.data.image.url,
              source: 'uploaded',
              altText: prev.images.primary.altText
            }
          }
        }));
        showMessage('success', '画像をアップロードしました');
      }
    } catch (error) {
      console.error('画像アップロードエラー:', error);
      showMessage('error', '画像のアップロードに失敗しました');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveProduct = async () => {
    try {
      // バリデーション
      if (!productForm.name || productForm.name.trim() === '') {
        showMessage('error', '商品名を入力してください');
        return;
      }

      if (!productForm.affiliateLinks?.moshimo?.url || productForm.affiliateLinks.moshimo.url.trim() === '') {
        showMessage('error', 'もしもアフィリエイトのURLを入力してください');
        return;
      }

      if (editingProduct) {
        // 更新
        const response = await adminApi.put(`/api/affiliate/admin/products/${editingProduct._id}`, productForm);
        if (response.data.success) {
          showMessage('success', '商品を更新しました');
          loadProducts();
          closeProductModal();
        }
      } else {
        // 新規作成
        const response = await adminApi.post('/api/affiliate/admin/products', productForm);
        if (response.data.success) {
          showMessage('success', '商品を作成しました');
          loadProducts();
          closeProductModal();
        }
      }
    } catch (error) {
      console.error('商品保存エラー:', error);
      showMessage('error', '商品の保存に失敗しました');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('本当に削除しますか？')) return;

    try {
      const response = await adminApi.delete(`/api/affiliate/admin/products/${productId}`);
      if (response.data.success) {
        showMessage('success', '商品を削除しました');
        loadProducts();
      }
    } catch (error) {
      console.error('商品削除エラー:', error);
      showMessage('error', '商品の削除に失敗しました');
    }
  };

  const handleToggleActive = async (productId) => {
    try {
      const response = await adminApi.patch(`/api/affiliate/admin/products/${productId}/toggle-active`);
      if (response.data.success) {
        showMessage('success', `商品を${response.data.isActive ? '有効' : '無効'}にしました`);
        loadProducts();
      }
    } catch (error) {
      console.error('ステータス切り替えエラー:', error);
      showMessage('error', 'ステータスの切り替えに失敗しました');
    }
  };

  const openProductModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm(product);
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        category: '醤油',
        brand: '',
        description: '',
        medicalRationale: '',
        freeFrom: [],
        images: {
          primary: {
            url: '',
            source: 'uploaded',
            altText: ''
          }
        },
        affiliateService: '',
        adsenseCode: '',
        rakutenType: '',
        rakutenRoomCode: '',
        affiliateLinks: {
          moshimo: {
            enabled: false,
            htmlCode: ''
          },
          rakutenDirect: {
            enabled: false,
            affiliateUrl: '',
            htmlCode: '',
            commissionRate: 0,
            isRateUpProduct: false
          },
          amazonDirect: {
            enabled: false,
            affiliateUrl: '',
            asin: ''
          }
        },
        displayLocations: [],
        relatedTags: [],
        isActive: true,
        displayOrder: 0
      });
    }
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setEditingProduct(null);
    setImageFile(null);
  };

  const handleFormChange = (field, value) => {
    setProductForm(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedFormChange = (path, value) => {
    setProductForm(prev => {
      const newForm = { ...prev };
      const keys = path.split('.');
      let current = newForm;

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newForm;
    });
  };

  // かんたんリンクHTMLパース関数
  const handleKantanLinkPaste = (e) => {
    const html = e.target.value;
    if (!html || html.trim().length === 0) return;

    try {
      // msmaflink関数の引数部分（JSON）を抽出
      const match = html.match(/msmaflink\((.*?)\);/s);
      if (!match) {
        alert('⚠️ かんたんリンクのHTMLコードが見つかりません');
        return;
      }

      const jsonStr = match[1];
      const data = JSON.parse(jsonStr);

      // 商品名をクリーニング
      let productName = data.n || '';

      // 不要な装飾文字を削除
      productName = productName
        .replace(/【[^】]*】/g, '') // 【...】を削除
        .replace(/＼[^／]*／/g, '') // ＼.../を削除
        .replace(/\\[^/]*\//g, '') // \.../ を削除
        .replace(/★/g, '')
        .replace(/◆/g, '')
        .replace(/▼/g, '')
        .replace(/■/g, '')
        .replace(/●/g, '')
        .replace(/\s+/g, ' ') // 連続スペースを1つに
        .trim();

      // 長すぎる場合は80文字で切る
      if (productName.length > 80) {
        productName = productName.substring(0, 80) + '...';
      }

      // 画像URLを抽出（最初の画像を使用）
      let imageUrl = '';
      if (data.d && data.c_p && data.p && data.p.length > 0) {
        imageUrl = data.d + data.c_p + data.p[0];
      }

      // アフィリエイトURLを構築（もしも tracking URL）
      // 既存商品（醤油・みりん）と同じ形式：
      // https://af.moshimo.com/af/c/click?a_id={a_id}&p_id={p_id}&pc_id={pc_id}&pl_id={pl_id}&url={encoded_target}
      let affiliateUrl = '';
      if (data.b_l && data.b_l.length > 0) {
        const link = data.b_l[0];
        const targetUrl = link.u_url || '';
        if (link.a_id && link.p_id && link.pl_id && link.pc_id && targetUrl) {
          const params = new URLSearchParams({
            a_id: link.a_id,
            p_id: link.p_id,
            pc_id: link.pc_id,
            pl_id: link.pl_id,
            url: targetUrl,
          });
          affiliateUrl = `https://af.moshimo.com/af/c/click?${params.toString()}`;
        } else {
          // フォールバック: パラメータが揃わない場合は生URLでも入れておく
          affiliateUrl = targetUrl;
        }
      }

      // フォームに自動入力
      // - url: 抽出した楽天URL（フォールバック用）
      // - htmlCode: もしも widget の原本HTML（widget埋め込み表示で報酬追跡されるため必須）
      setProductForm(prev => ({
        ...prev,
        name: productName,
        images: {
          ...prev.images,
          primary: {
            url: imageUrl,
            source: 'moshimo',
            altText: productName
          }
        },
        affiliateLinks: {
          ...prev.affiliateLinks,
          moshimo: {
            url: affiliateUrl,
            htmlCode: html,
            enabled: true
          }
        }
      }));

      alert('✅ 商品情報を自動入力しました。必要に応じて修正してください。');

    } catch (error) {
      console.error('かんたんリンクパースエラー:', error);
      alert('⚠️ HTMLコードの解析に失敗しました。正しいかんたんリンクのHTMLコードか確認してください。');
    }
  };

  const handleArrayToggle = (field, value) => {
    setProductForm(prev => {
      const array = prev[field] || [];
      const exists = array.includes(value);
      return {
        ...prev,
        [field]: exists
          ? array.filter(item => item !== value)
          : [...array, value]
      };
    });
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-dashboard__container">
          <p>読み込み中...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="admin-layout">
      <div className="affiliate-admin">
        <div className="admin-page-header">
          <button
            onClick={() => navigate('/admin')}
            className="btn-back"
          >
            ← 管理ダッシュボード
          </button>
          <h1 className="admin-page-title">アフィリエイト管理</h1>
        </div>

        {message.text && (
          <div className={`message message-${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="admin-tabs">
          <button
            className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            商品管理
          </button>
          <button
            className={`tab-button ${activeTab === 'widget' ? 'active' : ''}`}
            onClick={() => setActiveTab('widget')}
          >
            ウィジェット設定
          </button>
          <button
            className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            統計
          </button>
        </div>

        {activeTab === 'products' && (
          <div className="products-tab">
            <div className="tab-header">
              <h2>商品一覧</h2>
              <button className="btn-primary" onClick={() => openProductModal()}>
                + 新規商品追加
              </button>
            </div>

            {/* カテゴリーフィルター */}
            <div className="category-filter">
              {['all', '塩', '醤油', '味噌', '本みりん', '砂糖', 'にんにく', '生姜', 'グルテンフリー食品', 'サプリメント', '油・脂質', 'タンパク質源', '出汁・スープ', '時短食品'].map(category => (
                <button
                  key={category}
                  className={`filter-btn ${categoryFilter === category ? 'active' : ''}`}
                  onClick={() => setCategoryFilter(category)}
                >
                  {category === 'all' ? 'すべて' : category}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="loading">読み込み中...</div>
            ) : (
              <div className="products-list">
                {products.filter(p => categoryFilter === 'all' || p.category === categoryFilter).length === 0 ? (
                  <p className="no-products">商品が登録されていません</p>
                ) : (
                  <table className="products-table">
                    <thead>
                      <tr>
                        <th>画像</th>
                        <th>商品名</th>
                        <th>カテゴリ</th>
                        <th>ステータス</th>
                        <th>表示順</th>
                        <th>クリック数</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.filter(p => categoryFilter === 'all' || p.category === categoryFilter).map(product => (
                        <tr key={product._id}>
                          <td>
                            {product.images?.primary?.url ? (
                              <img
                                src={product.images.primary.url}
                                alt={product.name}
                                className="product-thumbnail"
                              />
                            ) : (
                              <div className="no-image">画像なし</div>
                            )}
                          </td>
                          <td>{product.name}</td>
                          <td>{product.category}</td>
                          <td>
                            <span className={`status-badge ${product.isActive ? 'active' : 'inactive'}`}>
                              {product.isActive ? '有効' : '無効'}
                            </span>
                          </td>
                          <td>{product.displayOrder}</td>
                          <td>{product.stats?.clickCount || 0}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-edit"
                                onClick={() => openProductModal(product)}
                              >
                                編集
                              </button>
                              <button
                                className="btn-toggle"
                                onClick={() => handleToggleActive(product._id)}
                              >
                                {product.isActive ? '無効化' : '有効化'}
                              </button>
                              <button
                                className="btn-delete"
                                onClick={() => handleDeleteProduct(product._id)}
                              >
                                削除
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'widget' && widgetConfig && (
          <div className="widget-tab">
            <h2>ウィジェット設定</h2>

            {/* 商品ウィジェット設定 */}
            <div className="widget-section">
              <h3>商品ウィジェット設定</h3>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={widgetConfig.productWidget?.enabled ?? true}
                    onChange={(e) => {
                      const newConfig = {
                        ...widgetConfig,
                        productWidget: {
                          ...widgetConfig.productWidget,
                          enabled: e.target.checked
                        }
                      };
                      setWidgetConfig(newConfig);
                    }}
                  />
                  商品ウィジェットを有効化
                </label>
              </div>

              <div className="form-group">
                <label>表示する最大商品数</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={widgetConfig.productWidget?.maxProductsPerWidget ?? 3}
                  onChange={(e) => {
                    const newConfig = {
                      ...widgetConfig,
                      productWidget: {
                        ...widgetConfig.productWidget,
                        maxProductsPerWidget: parseInt(e.target.value)
                      }
                    };
                    setWidgetConfig(newConfig);
                  }}
                />
              </div>

              <div className="form-group">
                <label>表示スタイル</label>
                <select
                  value={widgetConfig.productWidget?.displayStyle ?? 'carousel'}
                  onChange={(e) => {
                    const newConfig = {
                      ...widgetConfig,
                      productWidget: {
                        ...widgetConfig.productWidget,
                        displayStyle: e.target.value
                      }
                    };
                    setWidgetConfig(newConfig);
                  }}
                >
                  <option value="grid">グリッド表示</option>
                  <option value="list">リスト表示</option>
                  <option value="carousel">カルーセル表示（推奨）</option>
                  <option value="sidebar">サイドバー表示</option>
                </select>
              </div>

              <h4>自動表示設定</h4>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={widgetConfig.productWidget?.autoDisplay?.showOnRecipePage ?? true}
                    onChange={(e) => {
                      const newConfig = {
                        ...widgetConfig,
                        productWidget: {
                          ...widgetConfig.productWidget,
                          autoDisplay: {
                            ...widgetConfig.productWidget?.autoDisplay,
                            showOnRecipePage: e.target.checked
                          }
                        }
                      };
                      setWidgetConfig(newConfig);
                    }}
                  />
                  レシピページで自動表示
                </label>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={widgetConfig.productWidget?.autoDisplay?.showOnConsultationResult ?? true}
                    onChange={(e) => {
                      const newConfig = {
                        ...widgetConfig,
                        productWidget: {
                          ...widgetConfig.productWidget,
                          autoDisplay: {
                            ...widgetConfig.productWidget?.autoDisplay,
                            showOnConsultationResult: e.target.checked
                          }
                        }
                      };
                      setWidgetConfig(newConfig);
                    }}
                  />
                  栄養相談結果で自動表示
                </label>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={widgetConfig.productWidget?.autoDisplay?.showOnMyPage ?? false}
                    onChange={(e) => {
                      const newConfig = {
                        ...widgetConfig,
                        productWidget: {
                          ...widgetConfig.productWidget,
                          autoDisplay: {
                            ...widgetConfig.productWidget?.autoDisplay,
                            showOnMyPage: e.target.checked
                          }
                        }
                      };
                      setWidgetConfig(newConfig);
                    }}
                  />
                  マイページで自動表示
                </label>
              </div>

              <h4>表示対象プラン</h4>
              <div className="form-group">
                {['free', 'standard', 'premium', 'premium_plus'].map((plan) => (
                  <label key={plan} className="affiliate-admin__plan-label">
                    <input
                      type="checkbox"
                      checked={widgetConfig.productWidget?.showForPlans?.includes(plan) ?? (plan === 'free')}
                      onChange={(e) => {
                        const currentPlans = widgetConfig.productWidget?.showForPlans ?? ['free'];
                        const newPlans = e.target.checked
                          ? Array.from(new Set([...currentPlans, plan]))
                          : currentPlans.filter(p => p !== plan);

                        setWidgetConfig({
                          ...widgetConfig,
                          productWidget: {
                            ...widgetConfig.productWidget,
                            showForPlans: newPlans
                          }
                        });
                      }}
                    />
                    {plan === 'free' && ' 無料プラン'}
                    {plan === 'standard' && ' スタンダードプラン'}
                    {plan === 'premium' && ' プレミアムプラン'}
                    {plan === 'premium_plus' && ' プレミアムプラスプラン'}
                  </label>
                ))}
              </div>

              <button
                className="button button-primary"
                onClick={() => updateWidgetConfig({ productWidget: widgetConfig.productWidget })}
              >
                設定を保存
              </button>
            </div>

            {/* Google AdSense設定 */}
            <div className="widget-section">
              <h3>Google AdSense設定</h3>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={widgetConfig.adsense?.enabled ?? false}
                    onChange={(e) => {
                      const newConfig = {
                        ...widgetConfig,
                        adsense: {
                          ...widgetConfig.adsense,
                          enabled: e.target.checked
                        }
                      };
                      setWidgetConfig(newConfig);
                    }}
                  />
                  Google AdSenseを有効化
                </label>
              </div>

              <div className="form-group">
                <label>クライアントID（ca-pub-xxx）*</label>
                <input
                  type="text"
                  placeholder="ca-pub-1234567890123456"
                  value={widgetConfig.adsense?.clientId ?? ''}
                  onChange={(e) => {
                    const newConfig = {
                      ...widgetConfig,
                      adsense: {
                        ...widgetConfig.adsense,
                        clientId: e.target.value
                      }
                    };
                    setWidgetConfig(newConfig);
                  }}
                />
                <small className="form-hint">例: ca-pub-1859127931362916</small>
              </div>

              <div className="form-group">
                <label>スロットID *</label>
                <input
                  type="text"
                  placeholder="1234567890"
                  value={widgetConfig.adsense?.slotId ?? ''}
                  onChange={(e) => {
                    const newConfig = {
                      ...widgetConfig,
                      adsense: {
                        ...widgetConfig.adsense,
                        slotId: e.target.value
                      }
                    };
                    setWidgetConfig(newConfig);
                  }}
                />
                <small className="form-hint">例: 7282311726</small>
              </div>

              <div className="form-group">
                <label>広告フォーマット</label>
                <select
                  value={widgetConfig.adsense?.adFormat ?? 'auto'}
                  onChange={(e) => {
                    const newConfig = {
                      ...widgetConfig,
                      adsense: {
                        ...widgetConfig.adsense,
                        adFormat: e.target.value
                      }
                    };
                    setWidgetConfig(newConfig);
                  }}
                >
                  <option value="auto">自動サイズ（推奨）</option>
                  <option value="rectangle">レクタングル</option>
                  <option value="vertical">縦長バナー</option>
                  <option value="horizontal">横長バナー</option>
                </select>
              </div>

              <h4>表示対象プラン</h4>
              <div className="form-group">
                {['free', 'standard', 'premium', 'premium_plus'].map((plan) => (
                  <label key={plan} className="affiliate-admin__plan-label">
                    <input
                      type="checkbox"
                      checked={widgetConfig.adsense?.showForPlans?.includes(plan) ?? (plan === 'free')}
                      onChange={(e) => {
                        const currentPlans = widgetConfig.adsense?.showForPlans ?? ['free'];
                        const newPlans = e.target.checked
                          ? Array.from(new Set([...currentPlans, plan]))
                          : currentPlans.filter(p => p !== plan);

                        const newConfig = {
                          ...widgetConfig,
                          adsense: {
                            ...widgetConfig.adsense,
                            showForPlans: newPlans
                          }
                        };
                        setWidgetConfig(newConfig);
                      }}
                    />
                    {plan === 'free' && ' 無料プラン'}
                    {plan === 'standard' && ' スタンダードプラン'}
                    {plan === 'premium' && ' プレミアムプラン'}
                    {plan === 'premium_plus' && ' プレミアムプラスプラン'}
                  </label>
                ))}
              </div>

              <button
                className="button button-primary"
                onClick={() => updateWidgetConfig({ adsense: widgetConfig.adsense })}
              >
                設定を保存
              </button>
            </div>
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="stats-tab">
            <h2>統計情報</h2>
            <p className="coming-soon">統計情報画面（実装予定）</p>
          </div>
        )}

        {/* 商品編集モーダル */}
        {showProductModal && (
          <div className="modal-overlay" onClick={closeProductModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingProduct ? '商品編集' : '新規商品追加'}</h2>
                <button className="close-button" onClick={closeProductModal}>&times;</button>
              </div>

              <div className="modal-body">
                {/* かんたんリンクHTML貼り付け */}
                <div className="form-group">
                  <label>かんたんリンクHTML貼り付け（オプション）</label>
                  <textarea
                    value={productForm.affiliateLinks?.moshimo?.htmlCode || ''}
                    placeholder="もしもアフィリエイトで生成したかんたんリンクのHTMLコード全体を貼り付けると、自動的に情報を抽出します"
                    rows={4}
                    onChange={handleKantanLinkPaste}
                    className="affiliate-admin__code-textarea"
                  />
                  <small className="form-hint">
                    貼り付けると商品名・画像・リンクURLが自動入力されます（手動での修正も可能）
                  </small>
                </div>

                <hr className="affiliate-admin__separator" />

                {/* 商品名 */}
                <div className="form-group">
                  <label>商品名 *</label>
                  <input
                    type="text"
                    value={productForm.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder="井上古式じょうゆ 900ml"
                  />
                </div>

                {/* カテゴリー */}
                <div className="form-group">
                  <label>カテゴリー *</label>
                  <select
                    value={productForm.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                  >
                    <option value="塩">塩</option>
                    <option value="醤油">醤油</option>
                    <option value="味噌">味噌</option>
                    <option value="本みりん">本みりん</option>
                    <option value="砂糖">砂糖</option>
                    <option value="にんにく">にんにく</option>
                    <option value="生姜">生姜</option>
                    <option value="グルテンフリー食品">グルテンフリー食品</option>
                    <option value="サプリメント">サプリメント</option>
                    <option value="油・脂質">油・脂質</option>
                    <option value="タンパク質源">タンパク質源</option>
                    <option value="出汁・スープ">出汁・スープ</option>
                    <option value="時短食品">時短食品</option>
                  </select>
                  <small className="form-hint">
                    この商品が属するカテゴリーを選択してください。調味料設定ページの「💡 少しずつ体に優しい調味料に」セクションに表示されます。
                  </small>
                </div>

                {/* 商品画像 */}
                <div className="form-group">
                  <label>商品画像（オプション - かんたんリンクから自動取得）</label>
                  <div className="image-upload-area">
                    {productForm.images?.primary?.url ? (
                      <div className="image-preview">
                        <img src={productForm.images.primary.url} alt="商品画像" />
                        <button
                          className="btn-remove-image"
                          onClick={() => handleNestedFormChange('images.primary.url', '')}
                        >
                          削除
                        </button>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                        />
                        {uploadingImage ? 'アップロード中...' : '画像を選択'}
                      </div>
                    )}
                  </div>
                  <small className="form-hint">
                    かんたんリンクHTMLを貼り付けた場合、画像URLは自動的に設定されます。手動でアップロードする場合のみこのフィールドを使用してください。
                  </small>
                </div>

                {/* 商品説明 */}
                <div className="form-group">
                  <label>商品説明</label>
                  <textarea
                    value={productForm.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    placeholder="昔ながらの製法で作られた、ヨウ素控えめの醤油です"
                    rows={3}
                  />
                  <small className="form-hint">ウィジェットに表示される説明文（任意）</small>
                </div>

                {/* もしもアフィリエイト */}
                <div className="form-group">
                  <label>もしもアフィリエイト「かんたんリンク」URL *</label>
                  <input
                    type="url"
                    value={productForm.affiliateLinks?.moshimo?.url || ''}
                    onChange={(e) => handleNestedFormChange('affiliateLinks.moshimo.url', e.target.value)}
                    placeholder="https://af.moshimo.com/af/c/click?a_id=xxxxx"
                  />
                  <small className="form-hint">
                    もしもアフィリエイトで生成したリンクURLを貼り付けてください
                  </small>
                </div>

                {/* 表示場所（複数選択） */}
                <div className="form-group">
                  <label>表示場所（複数選択可）</label>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={productForm.displayLocations?.includes('form_page')}
                        onChange={(e) => {
                          const current = productForm.displayLocations || [];
                          const updated = e.target.checked
                            ? [...current, 'form_page']
                            : current.filter(l => l !== 'form_page');
                          handleFormChange('displayLocations', updated);
                        }}
                      />
                      <span>FormPage（相談結果画面）</span>
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={productForm.displayLocations?.includes('mypage')}
                        onChange={(e) => {
                          const current = productForm.displayLocations || [];
                          const updated = e.target.checked
                            ? [...current, 'mypage']
                            : current.filter(l => l !== 'mypage');
                          handleFormChange('displayLocations', updated);
                        }}
                      />
                      <span>MyPage（マイページ）</span>
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={productForm.displayLocations?.includes('recipe_page')}
                        onChange={(e) => {
                          const current = productForm.displayLocations || [];
                          const updated = e.target.checked
                            ? [...current, 'recipe_page']
                            : current.filter(l => l !== 'recipe_page');
                          handleFormChange('displayLocations', updated);
                        }}
                      />
                      <span>RecipePage（レシピページ）</span>
                    </label>
                  </div>
                </div>

                {/* 表示順 */}
                <div className="form-group">
                  <label>表示順</label>
                  <input
                    type="number"
                    value={productForm.displayOrder}
                    onChange={(e) => handleFormChange('displayOrder', parseInt(e.target.value))}
                    min={0}
                  />
                  <small className="form-hint">数字が大きいほどカルーセルの先頭に表示されます</small>
                </div>

                {/* ステータス */}
                <div className="form-group">
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={productForm.isActive}
                        onChange={(e) => handleFormChange('isActive', e.target.checked)}
                      />
                      <span>この商品を有効にする</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={closeProductModal}>
                  キャンセル
                </button>
                <button className="btn-primary" onClick={handleSaveProduct}>
                  保存
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateAdminPage;
